import { useCallback } from 'react';
import { Button, Dropdown, Field, Option } from '@fluentui/react-components';
import { Add20Regular, Delete20Regular } from '@fluentui/react-icons';
import { MultiUserSelect, withLockedUserIds } from '@modules/users';
import { TCustomGroup } from '@modules/users/types/organizations.types';
import { TFunctionRow } from '../helpers/functionRows.helper';

type TProps = {
  label: string;
  rows: TFunctionRow[];
  onChange: (rows: TFunctionRow[]) => void;
  /** Custom groups available for selection (backend already filters to active only). */
  customGroups: TCustomGroup[];
  /** Built-in role filter (AUTHOR, REVIEWER, APPROVER) */
  group: string;
  signedUserIds?: string[];
  disabled?: boolean;
  required?: boolean;
};

const FunctionUserSelect = ({
  label,
  rows,
  onChange,
  customGroups,
  group,
  signedUserIds = [],
  disabled,
  required,
}: TProps) => {
  const handleGroupChange = useCallback(
    (rowIndex: number, customGroupId: string) => {
      if (rows[rowIndex].userIds.some((userId) => signedUserIds.includes(userId))) return;
      const updated = rows.map((r, i) => (i === rowIndex ? { ...r, customGroupId, userIds: [] } : r));
      onChange(updated);
    },
    [rows, onChange, signedUserIds]
  );

  const handleUsersChange = useCallback(
    (rowIndex: number, userIds: string[]) => {
      const signedUserIdsInRow = rows[rowIndex].userIds.filter((userId) => signedUserIds.includes(userId));
      const nextUserIds = withLockedUserIds(userIds, signedUserIdsInRow);
      const updated = rows.map((r, i) => (i === rowIndex ? { ...r, userIds: nextUserIds } : r));
      onChange(updated);
    },
    [rows, onChange, signedUserIds]
  );

  const handleAddRow = useCallback(() => {
    const usedIds = new Set(rows.map((r) => r.customGroupId));
    const firstUnused = customGroups.find((g) => !usedIds.has(g.id));
    onChange([...rows, { customGroupId: firstUnused?.id || '', userIds: [] }]);
  }, [rows, onChange, customGroups]);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      if (rows[rowIndex].userIds.some((userId) => signedUserIds.includes(userId))) return;
      onChange(rows.filter((_, i) => i !== rowIndex));
    },
    [rows, onChange, signedUserIds]
  );

  const signedUserIdsSet = new Set(signedUserIds);

  return (
    <Field label={label} required={required}>
      <div className="flex flex-col space-y-2 border border-gray-200 rounded p-2">
        {rows.map((row, i) => {
          const usedByOthers = new Set(rows.filter((_, j) => j !== i).map((r) => r.customGroupId));
          const availableGroups = customGroups.filter((g) => !usedByOthers.has(g.id) || g.id === row.customGroupId);
          const signedUserIdsInRow = row.userIds.filter((userId) => signedUserIdsSet.has(userId));
          const rowHasSignedUsers = signedUserIdsInRow.length > 0;
          // Picking a group on an empty/inactive row wipes the user (handleGroupChange clears userIds), so lock it to remove-only.
          const isInvalidGroup = !row.customGroupId || !customGroups.some((g) => g.id === row.customGroupId);
          return (
            <div key={row.customGroupId || `empty-${i}`} className="flex items-center space-x-2">
              <Dropdown
                size="medium"
                className="w-44 flex-shrink-0"
                disabled={disabled || rowHasSignedUsers || isInvalidGroup}
                value={customGroups.find((g) => g.id === row.customGroupId)?.name || ''}
                selectedOptions={[row.customGroupId]}
                onOptionSelect={(_, data) => handleGroupChange(i, data.optionValue as string)}
              >
                {availableGroups.map((g) => (
                  <Option key={g.id} value={g.id}>
                    {g.name}
                  </Option>
                ))}
              </Dropdown>
              <div className="flex-1 min-w-0">
                <MultiUserSelect
                  selectedUserIds={row.userIds}
                  setSelectedUserIds={(ids) => handleUsersChange(i, ids)}
                  group={group}
                  customGroupId={row.customGroupId}
                  lockedUserIds={signedUserIdsInRow}
                  lockedUserMessage="This user has signed and cannot be removed."
                  excludedUserIds={signedUserIds.filter((userId) => !row.userIds.includes(userId))}
                  disabled={disabled || isInvalidGroup}
                  size="medium"
                />
              </div>
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                size="small"
                disabled={disabled || rowHasSignedUsers}
                onClick={() => handleDeleteRow(i)}
                aria-label="Remove function row"
              />
            </div>
          );
        })}
        <Button
          appearance="subtle"
          icon={<Add20Regular />}
          disabled={disabled || rows.length >= customGroups.length}
          onClick={handleAddRow}
          size="small"
        >
          Add function
        </Button>
      </div>
    </Field>
  );
};

export default FunctionUserSelect;
